import React, { useState, useMemo } from "react";
import {
    FaCalendarAlt, FaUsers, FaUserShield, FaChartLine,
    FaStore, FaClipboardCheck, FaExclamationTriangle, FaSearch, FaFilter
} from "react-icons/fa";
import BackMethods from "../../back_component/BackMethods";

export default function ActivityReportsPage() {
    const { backEnd_request } = BackMethods();

    const formatDate = (date) => date.toISOString().split('T')[0];

    const today = new Date();
    const [fromDate, setFromDate] = useState(formatDate(today));
    const [toDate, setToDate] = useState(formatDate(today));
    const [reportType, setReportType] = useState("patrols");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(""); // حالة البحث

    const handleReportTypeChange = (type) => {
        setReportType(type);
        setData([]);
        setSearchTerm("");
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const endpoint = reportType === "patrols"
                ? "/admin/reports/activity/by-user"
                : "/admin/reports/activity/by-staff";

            const res = await backEnd_request.get(endpoint, {
                params: { from: fromDate, to: toDate }
            });
            setData(res.data.data || []);
        } catch (err) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // منطق الفلترة حسب الاسم
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const name = (item.username || item.fullName || "").toLowerCase();
            return name.includes(searchTerm.toLowerCase());
        });
    }, [data, searchTerm]);

    const totals = filteredData.reduce((acc, curr) => ({
        establishments: acc.establishments + (curr.establishmentsCreated || 0),
        inspections: acc.inspections + (curr.inspectionVisitsCreated || curr.inspectionVisits || 0),
        violations: acc.violations + (curr.violationReportsCreated || curr.violationReports || 0),
    }), { establishments: 0, inspections: 0, violations: 0 });

    return (
        <div className="container-fluid py-4 bg-light min-vh-100 text-end" dir="rtl">
            {/* Header & Filters */}
            <div className="card shadow-sm border-0 mb-4 overflow-hidden">
                <div className="card-header bg-primary text-white py-3">
                    <h5 className="mb-0 fw-bold"><FaChartLine className="ms-2" /> لوحة تقارير النشاط للدوريات والموظفين</h5>
                </div>
                <div className="card-body bg-white">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-muted">من تاريخ</label>
                            <input type="date" className="form-control bg-light" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold text-muted">إلى تاريخ</label>
                            <input type="date" className="form-control bg-light" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small fw-bold text-muted">تصنيف التقرير</label>
                            <div className="d-flex gap-2">
                                <button className={`btn flex-fill ${reportType === 'patrols' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleReportTypeChange("patrols")}>حسب الدوريات</button>
                                <button className={`btn flex-fill ${reportType === 'staff' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleReportTypeChange("staff")}>حسب الموظفين</button>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-dark w-100 py-2 fw-bold" onClick={fetchReport} disabled={loading}>
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : "عرض التقرير"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <StatCard title="إجمالي المنشآت" value={totals.establishments} icon={<FaStore />} color="blue" />
                <StatCard title="إجمالي الزيارات " value={totals.inspections} icon={<FaClipboardCheck />} color="green" />
                <StatCard title="إجمالي الضبوط" value={totals.violations} icon={<FaExclamationTriangle />} color="orange" />
            </div>

            {/* البحث السريع */}
            {data.length > 0 && (
                <div className="row mb-3">
                    <div className="col-md-4 ms-auto">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-start-0"><FaFilter className="text-muted" /></span>
                            <input
                                type="text"
                                className="form-control border-end-0 shadow-none"
                                placeholder="ابحث عن طريق الاسم..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Data Table */}
            <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-dark text-white">
                            <tr>
                                {/* تم إصلاح المحاذاة هنا */}
                                <th className="py-3 px-4 text-start" style={{ width: '30%' }}>الاسم / الرقم</th>
                                {reportType === "staff" && <th className="text-center">المحافظة</th>}
                                <th className="text-center">المنشآت</th>
                                <th className="text-center">الزيارات </th>
                                <th className="text-center">الضبوط</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? filteredData.map((item, idx) => (
                                <tr key={idx}>
                                    <td className="px-4 text-start">
                                        <div className="d-flex align-items-center justify-content-start">
                                            <div className="avatar-circle ms-3">
                                                {reportType === "patrols" ? <FaUsers /> : <FaUserShield />}
                                            </div>
                                            <div className="text-start">
                                                <div className="fw-bold text-dark">{item.username || item.fullName}</div>
                                                <div className="text-muted small">ID: {item.userId || item.patrolStaffId}</div>
                                            </div>
                                        </div>
                                    </td>
                                    {reportType === "staff" && <td className="text-center"><span className="badge bg-light text-dark border">{item.governorate}</span></td>}
                                    <td className="text-center fw-bold text-primary">{item.establishmentsCreated}</td>
                                    <td className="text-center fw-bold text-success">{item.inspectionVisitsCreated || item.inspectionVisits}</td>
                                    <td className="text-center fw-bold text-danger">{item.violationReportsCreated || item.violationReports}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={reportType === "staff" ? 5 : 4} className="text-center py-5 text-muted">
                                        {loading ? "جاري التحميل..." : "لا توجد بيانات مطابقة للبحث"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .avatar-circle {
                    width: 38px; height: 38px; background: #f0f2f5; 
                    border-radius: 50%; display: flex; align-items: center; 
                    justify-content: center; color: #0d6efd; flex-shrink: 0;
                }
                .table thead th { font-size: 0.9rem; letter-spacing: 0.5px; }
            `}</style>
        </div>
    );
}

function StatCard({ title, value, icon, color }) {
    const colors = {
        blue: { bg: "#e7f1ff", text: "#0d6efd" },
        green: { bg: "#e8f5e9", text: "#2e7d32" },
        orange: { bg: "#fff3e0", text: "#ef6c00" }
    };
    const style = colors[color];
    return (
        <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `5px solid ${style.text}` }}>
                <div className="card-body d-flex align-items-center p-4">
                    <div className="rounded-circle p-3 ms-3" style={{ background: style.bg, color: style.text, fontSize: "1.5rem" }}>
                        {icon}
                    </div>
                    <div className="text-end">
                        <h6 className="text-muted mb-1 small fw-bold">{title}</h6>
                        <h3 className="mb-0 fw-bold">{value.toLocaleString()}</h3>
                    </div>
                </div>
            </div>
        </div>
    );
}